import { DIR, type Dir, type ViewRules } from '../engine';

export interface SwipeHandlers {
  readonly onDir: (d: Dir) => void;
}

/**
 * 스와이프 판정. touch-action:none 전제라 브라우저 제스처와 싸우지 않는다.
 *
 * **시간창 초과는 폐기가 아니라 재무장이다.** 아재개그를 3~10초 생각하는 것이 코어 루프인 게임에서
 * "화면에 손가락을 대고 생각하다 튕긴다"가 가장 흔한 입력인데, 폐기로 구현하면 그게 통째로 0커밋이 된다.
 * 재무장 원점은 현재 샘플이 아니라 **직전 샘플**이다 — 안드로이드는 이동 이벤트를 합쳐 보내므로
 * 현재 샘플로 당기면 커밋이 한 박자씩 늦는다.
 */
export function mountPointer(target: HTMLElement, R: ViewRules, h: SwipeHandlers): () => void {
  let trackId: number | null = null;
  let originX = 0;
  let originY = 0;
  let originT = 0;
  let lastX = 0;
  let lastY = 0;
  let lastT = 0;

  const down = (e: PointerEvent): void => {
    const inDeadzone =
      e.clientX < R.edgeDeadzonePx ||
      e.clientX > window.innerWidth - R.edgeDeadzonePx ||
      e.clientY < R.edgeDeadzonePx;
    // 데드존이면 추적을 점유하지 않는다 — 점유하면 그 뒤의 정상 스와이프까지 막힌다
    if (inDeadzone) {
      trackId = null;
      return;
    }
    trackId = e.pointerId;
    originX = e.clientX;
    originY = e.clientY;
    originT = e.timeStamp;
    lastX = originX;
    lastY = originY;
    lastT = originT;
  };

  const move = (e: PointerEvent): void => {
    if (trackId === null || e.pointerId !== trackId) return;

    // 재무장 — 원점을 직전 샘플로 당기고 판정을 계속한다
    if (e.timeStamp - originT > R.swipeWindowMs) {
      originX = lastX;
      originY = lastY;
      originT = lastT;
    }

    const dx = e.clientX - originX;
    const dy = e.clientY - originY;
    const adx = Math.abs(dx);
    const ady = Math.abs(dy);

    lastX = e.clientX;
    lastY = e.clientY;
    lastT = e.timeStamp;

    if (Math.max(adx, ady) < R.swipeMinDistPx) return;
    if (adx > ady * R.swipeAxisRatio) h.onDir(dx > 0 ? DIR.RIGHT : DIR.LEFT);
    else if (ady > adx * R.swipeAxisRatio) h.onDir(dy > 0 ? DIR.DOWN : DIR.UP);
    else return;

    // 한 제스처 안에서 두 번째 커밋을 허용한다(ㄱ자 선회)
    originX = e.clientX;
    originY = e.clientY;
    originT = e.timeStamp;
  };

  const up = (e: PointerEvent): void => {
    if (e.pointerId === trackId) trackId = null;
  };

  target.addEventListener('pointerdown', down, { passive: true });
  window.addEventListener('pointermove', move, { passive: true });
  window.addEventListener('pointerup', up, { passive: true });
  window.addEventListener('pointercancel', up, { passive: true });
  return () => {
    target.removeEventListener('pointerdown', down);
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', up);
    window.removeEventListener('pointercancel', up);
  };
}
